/**
 * 放送地点の新規作成 / 編集モーダル。
 *
 * - 左ペイン: 地点情報フォーム (説明 / 緯度 / 経度 / 半径 / 放送遅延) + コンテンツ編集
 * - 右ペイン: Leaflet 地図 (ピン + 半径円表示。ドラッグまたはクリックで位置指定)
 * - 半径スライダー操作で地図上の Circle がリアルタイムに同期する。
 * - spotId は UI 上に一切表示せず、編集対象を識別する内部値としてのみ保持する
 *   (新規作成時は undefined で、API 側が UUID v7 を採番する)。
 * - 1 地点に対して複数のコンテンツを設定可能。カテゴリ毎に必要な入力フィールドが
 *   動的に切り替わる。
 * - 保存ボタンで親に値を渡し、親側で API 呼び出し (POST/PUT) を実行する。
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';

import type { BroadcastContent, BroadcastPoint } from '../api/types';
import { parseRouteDirection, type RouteMaster } from '../data/routes';
import { fetchRouteShapePoints, type RouteShapePointsFile } from '../data/routeShapes';
import { findStationSegment, formatStationSegmentLabel, type RouteStop } from '../data/routeStops';
import { validateContent } from '../utils/broadcastContents';

import { BroadcastContentsEditor, type PreviewSsmlHandler } from './BroadcastContentsEditor';
import { BroadcastPointsMap } from './BroadcastPointsMap';

export interface BroadcastPointFormValues {
  routeDirection: string;
  /**
   * 内部的にのみ保持する spotId。UI 上には表示されず、API への識別子として渡す。
   * 新規作成時は undefined。
   */
  spotId?: string;
  description: string;
  latitude: number | undefined;
  longitude: number | undefined;
  radius_meters: number;
  broadcast_delay_seconds: number;
  contents: BroadcastContent[];
}

export interface BroadcastPointFormProps {
  /** モーダルのタイトルに表示するモード */
  mode: 'create' | 'edit';
  /** 編集対象 (新規作成時は routeDirection のみ与える) */
  initialValue: BroadcastPointFormValues;
  /** 同 routeDirection の他の地点 (地図にグレーで表示) */
  otherPoints: BroadcastPoint[];
  /**
   * 親が既に取得済みの当該方面の線形 GeoJSON (Point Feature の集合)。
   * - 親側 (BroadcastPointsPage) で選択中の routeDirection に対応する GeoJSON が
   *   ここに渡される。`null` の場合、フォーム側で `initialValue.routeDirection`
   *   から自前で fetch する (= 全件 scan モードのテーブル経由で編集を開いたケース)。
   */
  routeShapePoints: RouteShapePointsFile | null;
  /**
   * 線区マスタ。接続線遅延コンテンツの route_id ドロップダウンに使用する。
   * 省略時は空配列扱い。
   */
  routes?: RouteMaster[];
  /**
   * 駅順マスタ (route_id → 駅一覧)。接続線遅延コンテンツの開始駅 / 終了駅ドロップダウン用。
   * 親 (BroadcastPointsPage) が ControlPanel API 経由で事前取得して渡す。省略時は空。
   */
  routeStops?: Record<string, RouteStop[]>;
  /**
   * 任意テキスト (free_text) コンテンツの音声ファイルアップロード処理。
   * MP3 File を受け取り、保存された audio_key を返す。省略時はアップロード UI を無効化する。
   */
  onUploadAudio?: (file: File) => Promise<string>;
  /**
   * SSML / 任意テキストの音声プレビュー処理。省略時はプレビュー UI を無効化する。
   */
  onPreviewSsml?: PreviewSsmlHandler;
  /** 保存処理 (Promise を返す。throw でエラー表示) */
  onSubmit: (values: BroadcastPointFormValues) => Promise<void>;
  /** キャンセル */
  onClose: () => void;
}

const DEFAULT_RADIUS = 100;

/** 地図ポップアップの初期幅 (px) */
const MAP_POPUP_WIDTH = 520;
/** 地図ポップアップの初期高さ (px) — モーダルと同程度 (85vh相当) */
const MAP_POPUP_HEIGHT = Math.round(window.innerHeight * 0.85);

export function BroadcastPointForm(props: BroadcastPointFormProps): React.JSX.Element {
  const {
    mode,
    initialValue,
    otherPoints,
    routeShapePoints,
    routes,
    routeStops,
    onUploadAudio,
    onPreviewSsml,
    onSubmit,
    onClose,
  } = props;

  const [values, setValues] = useState<BroadcastPointFormValues>(() => normalize(initialValue));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** 地図ポップアップの表示状態 */
  const [mapOpen, setMapOpen] = useState(false);
  /** 「地図を開く」ボタンへの ref (ポップアップの位置計算に使う) */
  const mapButtonRef = useRef<HTMLButtonElement>(null);

  // ---------------------------------------------------------------------------
  // モーダルドラッグ
  // ---------------------------------------------------------------------------
  const [modalPos, setModalPos] = useState<{ top: number; left: number } | null>(null);
  const modalDragRef = useRef<{
    startX: number;
    startY: number;
    origLeft: number;
    origTop: number;
  } | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const handleModalHeaderMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
    if ((e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    const el = modalRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    modalDragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origLeft: rect.left,
      origTop: rect.top,
    };
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent): void => {
      if (!modalDragRef.current) return;
      const dx = e.clientX - modalDragRef.current.startX;
      const dy = e.clientY - modalDragRef.current.startY;
      setModalPos({
        left: Math.max(0, modalDragRef.current.origLeft + dx),
        top: Math.max(0, modalDragRef.current.origTop + dy),
      });
    };
    const handleMouseUp = (): void => {
      modalDragRef.current = null;
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  /**
   * 説明欄が「駅間判定による自動入力値」かどうか。
   * 新規作成時のみ、地図クリックで自動入力した直後は true となり、
   * 続けて地図を動かした際に説明を上書きしてよいと判断する。
   * ユーザーが説明を手入力で変更したら false に戻し、以後は自動上書きしない。
   *
   * NOTE: state ではなく ref で保持する。
   * 自動入力ロジックは `setValues` の関数更新コールバック内で参照されるが、
   * state を使うとクロージャに古い値がキャプチャされ、地図ドラッグ等で
   * 連続して `handleLocationChange` が呼ばれた際に 2 回目以降の上書きが
   * 効かなくなる (1 回目の `setDescriptionAutoFilled(true)` がまだ反映
   * されていないため `descriptionAutoFilled` が false のまま、かつ
   * `prev.description` は既に駅間ラベルになっており空ではないため、
   * `canAutoFill` 判定で false になってしまう)。
   * ref であれば同期的に値を更新でき、同一レンダー内の連続呼び出しでも
   * 正しく上書きが継続される。
   */
  const descriptionAutoFilledRef = useRef(false);

  /**
   * 編集対象の routeDirection に対応する線形 GeoJSON。
   * 親側 (BroadcastPointsPage) が同じ routeDirection 用に取得済みなら親値を使い、
   * それ以外 (例: 全件 scan モードのテーブルから別 routeDirection の編集を開いた場合) は
   * モーダル側で自前で取得する。
   */
  const [localShapePoints, setLocalShapePoints] = useState<RouteShapePointsFile | null>(null);

  // initialValue が変わった (= 別の地点を編集対象に切り替えた) 場合のみリセット
  useEffect(() => {
    setValues(normalize(initialValue));
    setError(null);
    descriptionAutoFilledRef.current = false;
  }, [initialValue]);

  // 親から線形が渡されていない (=null) 場合のみ、編集対象の routeDirection で
  // 自前 fetch する。親 props が変わって有効値になったタイミングでローカル取得は不要。
  useEffect(() => {
    if (routeShapePoints) {
      setLocalShapePoints(null);
      return;
    }
    const { routeId, directionName } = parseRouteDirection(values.routeDirection);
    if (!routeId || !directionName) {
      setLocalShapePoints(null);
      return;
    }
    let aborted = false;
    void (async () => {
      const data = await fetchRouteShapePoints(routeId, directionName);
      if (!aborted) setLocalShapePoints(data);
    })();
    return () => {
      aborted = true;
    };
  }, [routeShapePoints, values.routeDirection]);

  // 親値があれば優先、無ければローカル取得値を地図に渡す。
  const effectiveShapePoints = routeShapePoints ?? localShapePoints;

  /**
   * 緯度経度から駅間ラベルを判定して、新規作成時に説明欄へ自動入力するための共通処理。
   *
   * - 自動入力は新規作成時 (`mode === 'create'`) のみ。
   * - 説明が空 または 直前に自動入力した値の場合のみ上書きする (手入力は尊重)。
   * - 緯度経度が未確定 / 不正な場合は何もしない。
   *
   * 引数の values から次状態を返し、必要に応じて descriptionAutoFilled も更新する。
   */
  const applyAutoDescription = (
    next: BroadcastPointFormValues,
    prev: BroadcastPointFormValues,
  ): BroadcastPointFormValues => {
    if (mode !== 'create') return next;
    // 説明が空 または 自動入力済みの場合のみ上書き対象とする (手入力は尊重)。
    // ref を使うことで、`setValues` 関数更新コールバック内でも常に最新の
    // 「直前に自動入力したか」を参照できる (state クロージャ問題を回避)。
    const canAutoFill = descriptionAutoFilledRef.current || prev.description.trim() === '';
    if (!canAutoFill) return next;

    const label = computeStationSegmentLabel(
      routeStops,
      prev.routeDirection,
      next.latitude,
      next.longitude,
    );
    if (!label) return next;

    // 自動入力したことを記録し、続けて緯度経度を変更しても上書きできるようにする。
    descriptionAutoFilledRef.current = true;
    return { ...next, description: label };
  };

  const handleChange = <K extends keyof BroadcastPointFormValues>(
    key: K,
    value: BroadcastPointFormValues[K],
  ): void => {
    // 説明をユーザーが手入力で変更したら、以後は自動上書きを止める。
    if (key === 'description') {
      descriptionAutoFilledRef.current = false;
    }
    setValues((v) => {
      const next = { ...v, [key]: value };
      // 緯度 / 経度が変わった場合は駅間自動入力を試みる (新規作成時のみ)。
      if (key === 'latitude' || key === 'longitude') {
        return applyAutoDescription(next, v);
      }
      return next;
    });
  };

  /**
   * 地図上で位置が指定された (クリック / ドラッグ) ときのハンドラ。
   *
   * - 緯度経度を常に更新する。
   * - 新規作成時 (`mode === 'create'`) かつ、説明欄が空 または 直前に自動入力した値の
   *   場合のみ、駅順マスタから「開始駅-終了駅」を判定して説明欄へ自動セットする。
   *   ユーザーが手入力した説明は尊重して上書きしない。
   */
  const handleLocationChange = (lat: number, lng: number): void => {
    setValues((v) => {
      const next = { ...v, latitude: lat, longitude: lng };
      return applyAutoDescription(next, v);
    });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (submitting) return;
    setError(null);

    // バリデーション
    const validationError = validate(values);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(values);
    } catch (err) {
      setError((err as Error).message || '保存に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      data-testid="broadcast-point-form-modal"
      data-mode={mode}
    >
      <div
        ref={modalRef}
        className="modal modal--single-column"
        style={
          modalPos
            ? { position: 'fixed', top: modalPos.top, left: modalPos.left, transform: 'none' }
            : undefined
        }
      >
        <div
          className="modal-header"
          onMouseDown={handleModalHeaderMouseDown}
          style={{ cursor: 'grab' }}
        >
          <h2 style={{ margin: 0, fontSize: 15 }}>
            {mode === 'create' ? '放送地点を新規作成' : '放送地点を編集'} ({values.routeDirection})
          </h2>
          <button
            type="button"
            data-testid="modal-close-button"
            onClick={onClose}
            disabled={submitting}
            aria-label="閉じる"
          >
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit} style={{ display: 'contents' }}>
          <div className="modal-body modal-body--single-column">
            <div className="form-pane">
              <p className="form-required-hint">
                <span className="required-mark">*</span> は必須項目です
              </p>
              <label>
                <span className="required">説明</span>
                <input
                  type="text"
                  data-testid="description-input"
                  aria-label="説明"
                  value={values.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="例: 放送位置(代々木→新宿駅)"
                  required
                />
              </label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <label style={{ flex: 1 }}>
                  <span className="required">緯度</span>
                  <input
                    type="number"
                    step="0.000001"
                    data-testid="latitude-input"
                    aria-label="緯度"
                    placeholder="緯度"
                    value={values.latitude ?? ''}
                    onChange={(e) =>
                      handleChange(
                        'latitude',
                        e.target.value === '' ? undefined : Number(e.target.value),
                      )
                    }
                    required
                  />
                </label>
                <label style={{ flex: 1 }}>
                  <span className="required">経度</span>
                  <input
                    type="number"
                    step="0.000001"
                    data-testid="longitude-input"
                    aria-label="経度"
                    placeholder="経度"
                    value={values.longitude ?? ''}
                    onChange={(e) =>
                      handleChange(
                        'longitude',
                        e.target.value === '' ? undefined : Number(e.target.value),
                      )
                    }
                    required
                  />
                </label>
                {/* 地図を開くボタン */}
                <button
                  type="button"
                  ref={mapButtonRef}
                  className={mapOpen ? 'primary' : ''}
                  aria-label="地図で位置を選択"
                  aria-expanded={mapOpen}
                  onClick={() => setMapOpen((o) => !o)}
                  style={{ whiteSpace: 'nowrap', alignSelf: 'flex-end' }}
                >
                  📍 地図{mapOpen ? 'を閉じる' : 'を開く'}
                </button>
              </div>
              <label>
                <span className="required">半径 (m): {values.radius_meters}</span>
                <input
                  type="range"
                  min={10}
                  max={1000}
                  step={5}
                  data-testid="radius-input"
                  aria-label="半径"
                  value={values.radius_meters}
                  onChange={(e) => handleChange('radius_meters', Number(e.target.value))}
                />
              </label>
              <label>
                <span className="required">放送遅延 (秒)</span>
                <input
                  type="number"
                  min={0}
                  step={1}
                  data-testid="broadcast-delay-input"
                  aria-label="放送遅延"
                  value={values.broadcast_delay_seconds}
                  onChange={(e) => handleChange('broadcast_delay_seconds', Number(e.target.value))}
                />
              </label>

              <BroadcastContentsEditor
                contents={values.contents}
                routes={routes}
                routeStops={routeStops}
                onUploadAudio={onUploadAudio}
                onPreviewSsml={onPreviewSsml}
                onChange={(contents) => handleChange('contents', contents)}
              />
            </div>
          </div>
          <div className="modal-footer">
            {error ? (
              <div
                className="error-message"
                role="alert"
                style={{ flex: 1, margin: 0, textAlign: 'left' }}
              >
                {error}
              </div>
            ) : null}
            <button
              type="button"
              data-testid="cancel-button"
              onClick={onClose}
              disabled={submitting}
            >
              キャンセル
            </button>
            <button
              type="submit"
              data-testid="save-button"
              className="primary"
              disabled={submitting}
            >
              {submitting ? '保存中...' : '保存'}
            </button>
          </div>
        </form>
      </div>

      {/* 地図ポップアップ */}
      {mapOpen ? (
        <MapPopup
          width={MAP_POPUP_WIDTH}
          height={MAP_POPUP_HEIGHT}
          anchorRef={mapButtonRef}
          onClose={() => setMapOpen(false)}
          editingLatitude={values.latitude}
          editingLongitude={values.longitude}
          editingRadiusMeters={values.radius_meters || DEFAULT_RADIUS}
          otherPoints={otherPoints.filter((p) => p.spotId !== values.spotId)}
          routeShapePoints={effectiveShapePoints}
          onLocationChange={handleLocationChange}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 地図ポップアップ
// ---------------------------------------------------------------------------

interface MapPopupProps {
  width: number;
  height: number;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  onClose: () => void;
  editingLatitude: number | undefined;
  editingLongitude: number | undefined;
  editingRadiusMeters: number;
  otherPoints: BroadcastPoint[];
  routeShapePoints: RouteShapePointsFile | null;
  onLocationChange: (lat: number, lng: number) => void;
}

function MapPopup({
  width,
  height,
  anchorRef,
  onClose,
  editingLatitude,
  editingLongitude,
  editingRadiusMeters,
  otherPoints,
  routeShapePoints,
  onLocationChange,
}: MapPopupProps): React.JSX.Element {
  // ポップアップの位置 (left / top)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  // ドラッグ中かどうかを管理する ref (state にすると再レンダリングが多発するため ref を使う)
  const dragRef = useRef<{ startX: number; startY: number; origLeft: number; origTop: number } | null>(null);

  // 初期位置: アンカーボタンの直下に配置
  useEffect(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let left = rect.right - width;
    if (left < 8) left = 8;
    if (left + width > viewportWidth - 8) left = viewportWidth - width - 8;

    let top = rect.bottom + 8;
    if (top + height > viewportHeight - 8) top = rect.top - height - 8;
    if (top < 8) top = 8;

    setPos({ top, left });
  }, [anchorRef, width, height]);

  // ドラッグ開始
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
    // ヘッダー内のボタンはドラッグ対象外
    if ((e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    if (pos === null) return;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origLeft: pos.left,
      origTop: pos.top,
    };
  };

  // ドラッグ中 (window に登録して要素外でも追従)
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent): void => {
      if (!dragRef.current) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      const newLeft = Math.max(0, dragRef.current.origLeft + dx);
      const newTop = Math.max(0, dragRef.current.origTop + dy);
      setPos({ left: newLeft, top: newTop });
    };
    const handleMouseUp = (): void => {
      dragRef.current = null;
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const popupStyle: React.CSSProperties = {
    position: 'fixed',
    top: pos?.top ?? -9999,
    left: pos?.left ?? -9999,
    width,
    height,
    zIndex: 1100,
    resize: 'both',
    overflow: 'hidden',
    // 初期位置が決まるまで非表示
    visibility: pos ? 'visible' : 'hidden',
  };

  return (
    <div className="map-popup" style={popupStyle} role="dialog" aria-label="地図で位置を選択">
      <div
        className="map-popup__header"
        onMouseDown={handleMouseDown}
        style={{ cursor: 'grab' }}
      >
        <span className="map-popup__title">📍 地図で位置を選択</span>
        <button
          type="button"
          className="map-popup__close"
          aria-label="地図を閉じる"
          onClick={onClose}
        >
          ×
        </button>
      </div>
      <div className="map-popup__body">
        <BroadcastPointsMap
          editingLatitude={editingLatitude}
          editingLongitude={editingLongitude}
          editingRadiusMeters={editingRadiusMeters}
          otherPoints={otherPoints}
          routeShapePoints={routeShapePoints}
          onLocationChange={onLocationChange}
        />
      </div>
    </div>
  );
}

function normalize(v: BroadcastPointFormValues): BroadcastPointFormValues {
  return {
    routeDirection: v.routeDirection,
    spotId: v.spotId,
    description: v.description ?? '',
    latitude: v.latitude,
    longitude: v.longitude,
    radius_meters: v.radius_meters || DEFAULT_RADIUS,
    broadcast_delay_seconds: v.broadcast_delay_seconds ?? 0,
    contents: Array.isArray(v.contents) ? v.contents : [],
  };
}

/**
 * 緯度経度から駅間ラベル (「開始駅-終了駅」) を判定するピュア関数。
 * - 緯度経度が undefined / NaN の場合や、駅順マスタが空の場合は null。
 * - 該当する駅間が判定できない場合も null。
 *
 * BroadcastPointForm の自動入力ロジックから抽出してテスト可能にしたもの。
 */
export function computeStationSegmentLabel(
  routeStops: Record<string, RouteStop[]> | undefined,
  routeDirection: string,
  latitude: number | undefined,
  longitude: number | undefined,
): string | null {
  if (
    latitude === undefined ||
    longitude === undefined ||
    Number.isNaN(latitude) ||
    Number.isNaN(longitude)
  ) {
    return null;
  }
  const { routeId } = parseRouteDirection(routeDirection);
  const segment = findStationSegment(routeStops?.[routeId], latitude, longitude);
  const label = formatStationSegmentLabel(segment);
  return label || null;
}

/** 入力値のバリデーション。エラー文言を返す (null = 問題なし) */
export function validate(values: BroadcastPointFormValues): string | null {
  if (!values.routeDirection || !values.routeDirection.includes('#')) {
    return 'routeDirection は "<線区>#<方面>" 形式で必須です';
  }
  if (!values.description || values.description.trim() === '') {
    return '説明を入力してください';
  }
  if (values.latitude === undefined || Number.isNaN(values.latitude)) {
    return '緯度を入力してください';
  }
  if (values.longitude === undefined || Number.isNaN(values.longitude)) {
    return '経度を入力してください';
  }
  if (values.latitude < -90 || values.latitude > 90) {
    return '緯度は -90 〜 90 の範囲で指定してください';
  }
  if (values.longitude < -180 || values.longitude > 180) {
    return '経度は -180 〜 180 の範囲で指定してください';
  }
  if (values.radius_meters <= 0) {
    return '半径は 1 メートル以上で指定してください';
  }
  if (values.broadcast_delay_seconds < 0) {
    return '放送遅延は 0 以上で指定してください';
  }
  // コンテンツ毎のバリデーション
  for (let i = 0; i < values.contents.length; i++) {
    const err = validateContent(values.contents[i] as BroadcastContent, i);
    if (err) return err;
  }
  return null;
}
